import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, MusicNote, PenNib, Plus, Crown, Sparkle, ArrowRight, Star } from '@phosphor-icons/react';

const CORE_SERVICES = [
  {
    id: 'proofreading',
    icon: FileText,
    title: 'Proofreading',
    tagline: 'Novels, screenplays, short stories, poetry.',
    detail: 'Line-level correction with tracked changes side-by-side. Your voice preserved.',
    price: 'From £5',
    cta: 'Start proofreading',
    to: '/register?next=/',
    toAuthed: '/',
  },
  {
    id: 'song-analysis',
    icon: MusicNote,
    title: 'Song Analysis',
    tagline: 'Lyrics stay verbatim.',
    detail: 'Structural, imagery, and commercial-appeal analysis without altering a single lyric.',
    price: 'From £5',
    cta: 'Analyse a song',
    to: '/register?next=/',
    toAuthed: '/',
  },
  {
    id: 'rewrite',
    icon: PenNib,
    title: 'Rewrite',
    tagline: 'When proofreading isn\'t enough.',
    detail: 'Deep prose rewrite that lifts weak drafts while preserving intent. One-off flat fee.',
    price: '£10 flat',
    cta: 'Learn how it works',
    to: '/how-to',
    toAuthed: '/how-to',
  },
  {
    id: 'extensions',
    icon: Plus,
    title: 'Word Extensions',
    tagline: 'Top up when you need more.',
    detail: '3,000 / 10,000 / 25,000 / 50,000-word bundles for occasional heavy jobs.',
    price: 'From £3',
    cta: 'See top-ups',
    to: '/how-to',
    toAuthed: '/how-to',
  },
  {
    id: 'subscriptions',
    icon: Crown,
    title: 'Monthly Subscription',
    tagline: 'Regular writers — best value.',
    detail: 'Essential (£8) · Author (£15) · Professional (£30) — monthly word allowances that renew.',
    price: '£8–£30 / mo',
    cta: 'Compare plans',
    to: '/how-to',
    toAuthed: '/how-to',
  },
];

const ServiceCard = ({ svc, onClick, isAdmin }) => {
  const Icon = svc.icon;
  return (
    <button
      onClick={onClick}
      className="text-left panel-luxe p-7 md:p-8 hover:border-gold/40 transition-colors group flex flex-col h-full"
      data-testid={`service-card-${svc.id}`}
    >
      <div className="flex items-start justify-between mb-5">
        <Icon size={26} weight="duotone" className="text-gold" />
        {isAdmin ? (
          <span className="text-[10px] font-medium uppercase tracking-[0.28em] text-emerald-300">Complimentary</span>
        ) : (
          <span className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash">{svc.price}</span>
        )}
      </div>
      <h3 className="text-xl font-serif font-normal text-ivory mb-2 leading-tight">{svc.title}</h3>
      <p className="text-[13px] font-medium uppercase tracking-[0.24em] text-gold mb-4">{svc.tagline}</p>
      <p className="text-sm text-bone font-sans leading-relaxed flex-1">{svc.detail}</p>
      <span className="mt-6 inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.24em] text-ivory group-hover:text-gold transition-colors">
        {svc.cta}
        <ArrowRight size={12} weight="bold" className="transition-transform group-hover:translate-x-1" />
      </span>
    </button>
  );
};

export const ServicesSection = ({ isAuthenticated, isAdmin = false }) => {
  const navigate = useNavigate();
  const handleService = (svc) => navigate(isAuthenticated ? svc.toAuthed : svc.to);
  const handleCreativePartner = () => navigate('/creative-partner');

  return (
    <section className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-24" data-testid="services-section">
      <div className="text-center mb-14">
        <p className="text-[10px] font-medium uppercase tracking-[0.32em] text-gold mb-3">Our services</p>
        <h2 className="text-3xl md:text-5xl font-serif font-light tracking-tight text-ivory leading-[1.15]">
          Everything you need to <em className="text-gold not-italic font-normal">finish</em> your work.
        </h2>
      </div>

      {/* Core services grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 mb-8">
        {CORE_SERVICES.map((svc) => (
          <ServiceCard key={svc.id} svc={svc} isAdmin={isAdmin} onClick={() => handleService(svc)} />
        ))}
      </div>

      {/* Special service — Creative Partner */}
      <button
        onClick={handleCreativePartner}
        className="w-full text-left group relative overflow-hidden rounded-lg border border-gold/50 bg-gradient-to-br from-gold/10 via-card/40 to-transparent p-8 md:p-10 hover:border-gold transition-all"
        data-testid="service-card-creative-partner"
      >
        {/* SPECIAL ribbon */}
        <div className="absolute top-0 right-0">
          <div className="bg-gold text-charcoal text-[10px] font-medium uppercase tracking-[0.32em] px-5 py-1.5 flex items-center gap-1.5">
            <Star size={11} weight="fill" />
            {isAdmin ? 'Host · Complimentary' : 'Special service'}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr] gap-8 items-center">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <Sparkle size={22} weight="duotone" className="text-gold" />
              <p className="text-[10px] font-medium uppercase tracking-[0.32em] text-gold">
                {isAdmin ? 'Premium · complimentary for the host' : 'Premium yearly membership'}
              </p>
            </div>
            <h3 className="text-3xl md:text-4xl font-serif font-light text-ivory leading-[1.1] mb-4">
              Creative <em className="text-gold not-italic font-normal">Partner</em>
            </h3>
            <p className="text-base md:text-lg text-bone font-serif font-light leading-relaxed mb-4">
              A personalised AI development partner that remembers every character, idea and deadline —
              from concept to completed pitch.
            </p>
            <p className="text-sm text-ash font-sans leading-relaxed">
              Six expert partners on-call: <span className="text-gold">Maya, Alex, Samantha, Lawrence, Keith &amp; Noah</span>.
            </p>
          </div>

          <div className="flex flex-col items-start md:items-end gap-4">
            {isAdmin ? (
              <div className="flex items-baseline gap-2">
                <span className="text-4xl md:text-5xl font-serif font-light text-emerald-300">Free</span>
                <span className="text-xs text-ash font-sans">· Host</span>
              </div>
            ) : (
              <div className="flex items-baseline gap-2">
                <span className="text-5xl md:text-6xl font-serif font-light text-ivory">£79</span>
                <span className="text-xs text-ash font-sans">/ year</span>
              </div>
            )}
            <span className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.24em] text-gold group-hover:text-ivory transition-colors">
              {isAdmin ? 'Enter your studio' : 'Meet your partners'}
              <ArrowRight size={14} weight="bold" className="transition-transform group-hover:translate-x-1" />
            </span>
          </div>
        </div>
      </button>
    </section>
  );
};
